import { useCallback } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { Alert } from '@cmsgov/ds-medicare-gov'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { MspSortForm } from 'models/claims'
import { logAriaMessage } from 'redux/reducers/aria-log'
import { MspCaseResults } from './MspCaseResults'
import { MspCaseSort } from './MspCaseSort'
import { Status } from 'redux/helpers/claimCase'
import {
  getSearchResults,
  getSearchResultsTotal,
  getSortDirection,
  getSortType,
  getStatus,
  showAllSearchResults,
  showMoreSearchResults,
  sortCases
} from 'redux/reducers/msa'
import { useFetchOifCookiesOnLoad } from 'containers/Claims/hooks'

export const MsaContent = () => {
  const classType = 'msa'
  const { t } = useTranslation(classType)
  const dispatch = useDispatch()
  const msaStatus = useSelector(getStatus)
  const oifCookieLoadState = useFetchOifCookiesOnLoad(msaStatus)
  const msaCaseResults = useSelector(getSearchResults)
  const msaTotalCaseResults = useSelector(getSearchResultsTotal)
  const msaSortDirection = useSelector(getSortDirection)
  const msaSortType = useSelector(getSortType)
  const msaIsLoading = msaStatus === Status.LOADING || oifCookieLoadState === 'loading'
  const msaIsError = msaStatus === Status.ERROR
  const msaIsFinished = msaStatus === Status.FINISHED && oifCookieLoadState !== 'loading'

  const handleMsaSubmit: SubmitHandler<MspSortForm> = useCallback(
    (formValues: MspSortForm) => {
      dispatch(sortCases({ sortType: formValues.sortType, sortDirection: formValues.sortDirection }))
      dispatch(logAriaMessage(t('sortComplete')))
    },
    [dispatch, t]
  )

  const handleMsaShowMoreSearchResults = () => {
    dispatch(showMoreSearchResults())
  }

  const handleMsaShowAllSearchResults = () => {
    dispatch(showAllSearchResults())
  }

  return (
    <>
      <div className="ds-l-row">
        <div className="ds-l-col--12">
          {msaIsError && (
            <Alert
              className="ds-u-margin-bottom--3"
              variation="warn"
              role="alert"
            >
              {t('summary.errorAlert')}
            </Alert>
          )}

          <p>{t('summary.description')}</p>
          <Alert hideIcon className="ds-u-margin-y--3">
            {t('summary.popupAlert')}
          </Alert>
        </div>
      </div>
      {msaIsLoading && <MySpinner />}
      {!msaIsLoading && !msaIsError && (
        <>
          <div>
            <MspCaseSort
              classType={classType}
              sortDirection={msaSortDirection}
              sortType={msaSortType}
              onSubmit={handleMsaSubmit}
              className="ds-u-border-top--1 ds-u-padding-top--3"
            />
          </div>
          <div className="ds-u-margin-top--3">
            <MspCaseResults
              classType={classType}
              isLoading={msaIsLoading}
              isError={msaIsError}
              isFinished={msaIsFinished}
              results={msaCaseResults}
              totalCaseResults={msaTotalCaseResults}
              onShowMoreSearchResults={handleMsaShowMoreSearchResults}
              onShowAllSearchResults={handleMsaShowAllSearchResults}
              className="ds-u-border-top--1 ds-u-padding-top--3"
            />
          </div>
        </>
      )}
    </>
  )
}
